/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Typography, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import mixins from '../../assets/styles/mixins.scss';
import AlertIcon from '../icons/AlertIcon';

const useCustomBannerStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    maxHeight: 22,
    padding: '8px 12px',
    borderRadius: 4,
    fontSize: mixins.fontSize14,
    '&.alert': {
      backgroundColor: '#FFE7E5',
      color: '#856404',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '3px',
        backgroundColor: '#FF362A',
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px'
      }
    },
    '&.ok': {
      backgroundColor: '#d4edda',
      color: '#155724',
      border: '1px solid #c3e6cb'
    },
    '&.danger': {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      border: '1px solid #f5c6cb'
    },
    '&.statement': {
      backgroundColor: mixins.color_primary,
      color: mixins.chalkWhiteFont,
      border: 'none'
    }
  },
  headerIcon: {
    fontSize: 16,
    '&.alert': {
      color: '#856404'
    },
    '&.ok': {
      color: '#155724'
    },
    '&.danger': {
      color: '#721c24'
    },
    '&.statement': {
      color: mixins.chalkWhiteFont
    }
  },
  closeButton: {
    marginLeft: 'auto',
    padding: '4px',
    color: '#000000'
  }
});

/**
 * CustomBanner Component
 * Renders a banner with icon and text based on type
 */
const CustomBanner = ({ type, icon, text, className, onClose }) => {
  const classes = useCustomBannerStyles();

  // Render AlertIcon for alert, ok, and danger types
  const renderIcon = () => {
    if (icon) {
      return <div className={`${classes.headerIcon} ${type || ''}`}>{icon}</div>;
    }

    if (type === 'alert' || type === 'ok' || type === 'danger') {
      return (
        <div className={`${classes.headerIcon} ${type}`}>
          <AlertIcon fontSize="inherit" />
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`${classes.header} ${type || ''} ${className || ''}`}>
      {renderIcon()}
      <Typography variant="body2">{text}</Typography>
      {onClose && (
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

CustomBanner.propTypes = {
  type: PropTypes.oneOf(['alert', 'ok', 'danger', 'statement']),
  icon: PropTypes.node,
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
  onClose: PropTypes.func
};

CustomBanner.defaultProps = {
  type: 'statement',
  icon: null,
  className: '',
  onClose: () => {}
};


export default CustomBanner;
